import React, { useEffect, useState } from 'react'
import { Card, Form, Button, Row, Col, DatePicker, Spin, Table, Switch } from 'antd'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'
import BaseListing from 'src/BaseListings'
import { lzNotifyErrorMessageFromResult } from 'src/helpers/commons/notifications'
import { wtApiCall, wtApiPostWithContentTypeHandling } from 'src/helpers/commons/ApiWrapper'
import { wtFormatRoundedValue, wtRoundValue } from 'src/helpers/commons/utils'
import WidgetSelector from 'src/widgets/Selectors/WidgetSelector'
import {
  SALE_ORDER_INVOICES_DATE_NAMES,
  SALE_ORDER_INVOICES_DATE_TYPE_DROPDOWN_OPTIONS,
  SALE_ORDER_INVOICES_STATUS_DROPDOWN_OPTIONS,
  SALE_ORDER_INVOICES_STATUS_NAME,
} from 'src/models/sale-order-invoice'
import WidgetBranchSelector from 'src/widgets/Selectors/WidgetBranchSelector'
import WidgetProjectSelector from 'src/widgets/Selectors/WidgetProjectSelector'
import WidgetCustomerSelector from 'src/widgets/Selectors/WidgetCustomerSelector'
import WidgetItemSelector from 'src/widgets/Selectors/WidgetItemSelector'
import { useMasterDateTimeFormats } from 'src/helpers/commons/masterDateFormat'

const SaleOrderInvoiceDetailReport = () => {
  const Base = new BaseListing('sale_book_report')
  const { formatDate } = useMasterDateTimeFormats()
  const [isLoading, setIsLoading] = useState(false)
  const [form] = Form.useForm()
  const branches = wtGetFromLocalStorage('branches')
  const [customers, setCustomers] = useState([])
  const [projects, setProjects] = useState([])
  const [dateRange, setDateRange] = useState([])
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [ProjectId, setProjectId] = useState(null)
  const [ProjectName, setProjectName] = useState('')
  const [PartyName, setPartyName] = useState('')
  const [PartyId, setPartyId] = useState(null)
  const [statusId, setStatusId] = useState(0)
  const [branchId, setBranchId] = useState(0)
  const [branchName, setBranchName] = useState('')
  const [SaleOrderInvoiceDetailReportData, setSaleOrderInvoiceDetailReportData] = useState([])
  const [searchMore, setSearchMore] = useState(false)
  const [dateType, setDateType] = useState(0)
  const [ItemName, setItemName] = useState('')
  const [ItemId, setItemId] = useState('')
  const [Items, setItems] = useState([])

  const changeValues = (key, val) => {
    setSaleOrderInvoiceDetailReportData([])
    if (key === 'project_id') {
      setProjectId(val)
      const selectedProject = projects.find((p) => p.value === val)
      setProjectName(selectedProject ? selectedProject.title : '')
    }
    if (key === 'branch_id') {
      setBranchId(val.value)
      setBranchName(val.label)
    }
    if (key === 'date_range') {
      setDateRange(val)
    }
    if (key === 'customer_id') {
      setPartyId(val)
      if (val === null) {
        setPartyName('')
      }
    }
    if (key === 'item_id') {
      setItemId(val.value)
      setItemName(val.label)
    }
    if (key === 'status') {
      if (val === null) {
        setStatusId(0)
      }
      setStatusId(val?.value)
    }
    if (key === 'date_type') {
      if (val === null) {
        setDateType(0)
      }
      setDateType(val?.value)
    }
  }

  useEffect(() => {
    Base.dropdownData('customers,projects,items', {}, [setCustomers, setProjects, setItems], 1)
  }, [])

  const getReportData = () => {
    if (dateRange.length < 2) {
      lzNotifyErrorMessageFromResult('Please select date range')
      return
    }
    const fromDate = dateRange[0]
    const toDate = dateRange[1]
    setIsLoading(true)
    wtApiCall(
      'reports/sale_order_invoice_detail_report',
      {
        from_date: fromDate.format('YYYY-MM-DD'),
        to_date: toDate.format('YYYY-MM-DD'),
        party_id: PartyId,
        project_id: ProjectId,
        status: statusId,
        date_type: dateType,
        branch_id: branchId,
        item_id: ItemId,
      },
      'get',
      (response) => {
        setSaleOrderInvoiceDetailReportData(response.data)
        setIsLoading(false)
      },
      (error) => {
        lzNotifyErrorMessageFromResult(error)
        setIsLoading(false)
      },
    )
  }

  const exportToExcel = () => {
    setIsLoading(true)
    wtApiPostWithContentTypeHandling('sale_order_invoice_detail_report/export', {
      data: SaleOrderInvoiceDetailReportData,
    })
    setIsLoading(false)
  }

  const columns = [
    {
      key: 'sr',
      title: 'SR',
      render: (text, record, index) => index + 1, // Adds Serial Number
      fixed: 'left',
      width: 50,
    },
    {
      key: 'code',
      title: 'Inv No#',
      dataIndex: 'code',
      fixed: 'left',
      width: 60,
      render: (text, record) => (
        <span
          onClick={() =>
            window.open(`/sale_order_invoices#view:${record.sale_order_invoice.id}`, '_blank')
          }
          style={{ color: 'blue', cursor: 'pointer', textDecoration: 'none' }}
        >
          {record.sale_order_invoice.code}
        </span>
      ),
    },
    {
      key: 'code',
      title: 'Gst Inv No#',
      dataIndex: 'code',
      width: 80,
      render: (text, record) => record.sale_order_invoice.invoice_reference_no,
    },
    {
      key: 'invoice_date',
      title: 'Inv Date',
      dataIndex: 'invoice_reference_date',
      width: 85,
      render: (text, record) => formatDate(record.sale_order_invoice.invoice_reference_date),
    },
    {
      key: 'do_no',
      title: 'DO No#',
      dataIndex: 'code',
      width: 60,
      render: (text, record) => record.sale_order_delivery.code,
    },
    {
      key: 'invoice_date',
      title: 'Varification Date',
      dataIndex: 'invoice_date',
      width: 85,
      render: (text, record) => formatDate(record.sale_order_invoice.invoice_date),
    },
    {
      key: 'code',
      title: 'SO No#',
      dataIndex: 'code',
      width: 80,
      render: (text, record) => record.sale_order.code,
    },
    {
      key: 'delivery_date',
      title: 'DO Date',
      dataIndex: 'delivery_date',
      width: 90,
      render: (text, record) => formatDate(record.sale_order_delivery.delivery_date),
    },
    {
      key: 'code',
      title: 'Branch',
      dataIndex: 'branch',
      width: 120,
      render: (text, record) => record.sale_order_invoice.branch.name,
    },
    {
      key: 'project',
      title: 'Project',
      dataIndex: 'project',
      width: 170,
      render: (text, record) => record.sale_order_invoice.project.name,
    },
    {
      key: 'party',
      title: 'Customer',
      dataIndex: 'party',
      width: 170,
      render: (text, record) => record.sale_order_invoice.party.name,
    },
    {
      key: 'revenue_account',
      title: 'Revenue Account',
      dataIndex: 'revenue_account_id',
      width: 150,
      render: (text, record) =>
        record.sale_order.revenue_account ? record.sale_order.revenue_account.name : '',
    },
    {
      key: 'item_id',
      title: 'Cost Account',
      dataIndex: 'item_name',
      render: (text, record) => (record.item.cost_account ? record.item.cost_account.name : ''),
      width: 150,
    },
    {
      key: 'inventory_account_id',
      title: 'Inventory Account',
      dataIndex: 'inventory_account',
      render: (text, record) =>
        record.item.inventory_account ? record.item.inventory_account.name : '',
      width: 150,
    },
    {
      key: 'item_id',
      title: 'Item Name',
      dataIndex: 'item_name',
      width: 150,
    },
    {
      key: 'item_unit_id',
      title: 'Item Unit',
      dataIndex: 'item_unit_id',
      render: (text, record) => (record.item_unit ? record.item_unit.name : ''),
      width: 80,
    },
    {
      key: 'item_brand_id',
      title: 'Item Brand',
      dataIndex: 'item_brand_id',
      render: (text, record) => record.item_brand.name,
      width: 150,
    },
    {
      key: 'invoice_quantity',
      title: 'Delivering Qty',
      dataIndex: 'invoice_quantity',
      width: 90,
      render: (text, record) => wtFormatRoundedValue(record.invoice_quantity, '', false, 2),
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.unit_price, '', false, 2),
    },
    {
      title: 'Discount Amount',
      dataIndex: 'discount_amount',
      key: 'discount_amount',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.discount_amount, '', false, 2),
    },
    {
      title: 'Sub total',
      dataIndex: 'total_price',
      key: 'total_price',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_price, '', false, 2),
    },
    {
      title: 'Tax Rate',
      dataIndex: 'tax_rate',
      key: 'tax_rate',
      width: 60,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.tax_rate, '', false, 2),
    },
    {
      title: 'Tax Amount',
      dataIndex: 'tax_amount',
      key: 'tax_amount',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.tax_amount, '', false, 2),
    },
    {
      title: 'Net Total',
      dataIndex: 'net_total',
      key: 'net_total',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.net_total, '', false, 2),
    },
    {
      title: 'Unit Cost',
      dataIndex: 'unit_cost',
      key: 'unit_cost',
      width: 70,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.unit_cost, '', false, 2),
    },
    {
      title: 'Total Cost',
      dataIndex: 'total_cost',
      key: 'total_cost',
      width: 80,
      ellipsis: true,
      render: (text, record) => wtFormatRoundedValue(record.total_cost, '', false, 2),
    },
    {
      key: 'warehouse',
      title: 'Warehouse',
      dataIndex: 'warehouse',
      width: 100,
      render: (text, record) => record.sale_order_delivery.warehouse.name,
    },
  ]

  return (
    <div
      className="container-fluid"
      style={{
        padding: '1em',
        backgroundColor: 'white',
        minHeight: '100vh',
        fontFamily: 'Helvetica',
      }}
    >
      <Form
        layout={'vertical'}
        form={form}
        onFinish={getReportData}
        style={{ maxWidth: '100%', margin: '0 auto' }}
      >
        <Card
          style={{ width: '100%' }}
          title={
            <span style={{ fontSize: '26px', marginLeft: '40%' }}>
              Sale Order Invoice Detail Report
            </span>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={15} sm={10} md={5}>
              <label>Date Range: *</label>
              <DatePicker.RangePicker
                onChange={(value) => {
                  setDateRange(value)
                  if (value && value.length === 2) {
                    setFromDate(value[0].format('YYYY-MM-DD'))
                    setToDate(value[1].format('YYYY-MM-DD'))
                  }
                }}
                style={{ width: '100%', height: 35 }}
                value={dateRange}
              />
            </Col>
            <Col xs={15} sm={10} md={4}>
              <label>Date Type:</label>
              <WidgetSelector
                options={SALE_ORDER_INVOICES_DATE_TYPE_DROPDOWN_OPTIONS}
                selectedValue={dateType}
                displayValue={SALE_ORDER_INVOICES_DATE_NAMES[dateType]}
                onChange={(values) => changeValues('date_type', values)}
              />
            </Col>
            <Col xs={15} sm={10} md={5}>
              <label>Status:</label>
              <WidgetSelector
                options={SALE_ORDER_INVOICES_STATUS_DROPDOWN_OPTIONS}
                selectedValue={statusId}
                displayValue={SALE_ORDER_INVOICES_STATUS_NAME[statusId]}
                onChange={(values) => changeValues('status', values)}
              />
            </Col>

            <Col xs={15} sm={10} md={5}>
              <label>Branch:</label>
              <WidgetBranchSelector
                value={branchId}
                displayValue={branchName}
                branches={branches}
                placeholder="Select Branch"
                onChange={(values) => changeValues('branch_id', values)}
                showCloudIcon={false}
              />
            </Col>

            <Col xs={15} sm={10} md={5}>
              <Button
                style={{ marginTop: 30, marginRight: 5 }}
                type="primary"
                htmlType="submit"
                disabled={isLoading ? true : false}
              >
                Search
              </Button>
              <Button
                style={{ marginTop: 30, marginRight: 5, backgroundColor: '#4BB543', color: '#fff' }}
                onClick={exportToExcel}
                disabled={isLoading ? true : false}
              >
                Export
              </Button>
              <Button
                style={{ marginTop: 30, marginRight: 5, color: '#fff', backgroundColor: '#E29C06' }}
                onClick={() => setSearchMore(!searchMore)}
                disabled={isLoading ? true : false}
              >
                {searchMore ? 'Less...' : 'More...'}
              </Button>
            </Col>
          </Row>

          {searchMore && (
            <Row gutter={[24, 24]}>
              <Col xs={15} sm={10} md={5}>
                <label>Customer</label>
                <WidgetCustomerSelector
                  onChange={(value) => changeValues('customer_id', value)}
                  customers={customers}
                  displayValue={PartyName}
                  value={PartyId}
                  reloadDropdownData={() => reloadParties()}
                />
              </Col>
              <Col xs={15} sm={10} md={5}>
                <label>Project:</label>
                <WidgetProjectSelector
                  onChange={(value) => changeValues('project_id', value)}
                  data={projects} // <-- Use the formatted treeData here
                  value={ProjectId}
                  displayValue={ProjectName}
                  height={35}
                  reloadDropdownData={() => Base.dropdownData('projects', {}, [setProjects], 1)}
                />
              </Col>
              <Col xs={15} sm={10} md={5}>
                <label>Item Name:</label>
                <WidgetItemSelector
                  items={Items}
                  onChange={(value) => changeValues('item_id', value)}
                  displayValue={ItemName}
                  reloadDropdownData={() => Base.dropdownData('items', {}, [setItems], 1)}
                  placeHolder={'Select Item'}
                  value={ItemId}
                  // size={'sm'}
                />
              </Col>
            </Row>
          )}
        </Card>
      </Form>

      <Card style={{ marginTop: 30 }}>
        <Spin spinning={isLoading}>
          <Table
            columns={columns}
            dataSource={SaleOrderInvoiceDetailReportData}
            pagination={false}
            bordered
            size="small"
            scroll={{ x: 2100, y: 400 }}
            virtual={true}
            loading={{
              indicator: (
                <div>
                  <Spin />
                </div>
              ),
              spinning: isLoading,
            }}
          />
        </Spin>
      </Card>
    </div>
  )
}
export default SaleOrderInvoiceDetailReport
